import React, { useMemo, useState } from 'react';
import { GripVertical, X } from 'lucide-react';

export const ID_TOKENS = [
  { id: 'sender', label: 'Sender hub', sample: 'KPM' },
  { id: 'receiver', label: 'Receiver hub', sample: 'HYD' },
  { id: 'company', label: 'Company', sample: 'ATHN' },
  { id: 'seq', label: 'Number', sample: '46', required: true },
  { id: 'fy', label: 'FY 26-27', sample: '26-27' },
  { id: 'yy', label: 'Year 26', sample: '26' },
  { id: 'yyyy', label: 'Year 2026', sample: '2026' },
  { id: 'mlet', label: 'Month letter', sample: 'J' },
];

const TOKEN_IDS = ID_TOKENS.map((t) => t.id);
const TOKEN_RE = /\{(sender|receiver|company|seq|fy|yy|yyyy|mlet)\}/gi;
const SEPS = ['-', '/', '.', '_'];
const DND = 'text/plain';

const tokenMeta = (id) => ID_TOKENS.find((t) => t.id === id) || { id, label: id, sample: id };

export const previewDocumentId = (fmt) => {
  const samples = Object.fromEntries(ID_TOKENS.map((t) => [t.id, t.sample]));
  return String(fmt || '').replace(/\{(sender|receiver|company|seq|fy|yy|yyyy|mlet)\}/gi, (_, key) => (
    samples[String(key).toLowerCase()] || ''
  ));
};

export const parseIdFormat = (fmt) => {
  const text = String(fmt || '');
  const tokens = [];
  const seps = [];
  let last = 0;
  let match;
  const re = new RegExp(TOKEN_RE);
  while ((match = re.exec(text))) {
    const id = match[1].toLowerCase();
    if (tokens.length) {
      const between = text.slice(last, match.index);
      seps.push(/^[.\-/_]+$/.test(between) ? between : '-');
    }
    if (!tokens.includes(id)) tokens.push(id);
    last = match.index + match[0].length;
  }
  if (!tokens.includes('seq')) {
    tokens.push('seq');
    if (tokens.length > 1) seps.push('-');
  }
  while (seps.length < Math.max(0, tokens.length - 1)) seps.push('-');
  return { tokens, seps: seps.slice(0, Math.max(0, tokens.length - 1)) };
};

export const serializeIdFormat = ({ tokens, seps }) => {
  const parts = [];
  (tokens || []).forEach((id, i) => {
    if (!TOKEN_IDS.includes(id)) return;
    if (parts.length) parts.push(seps[i - 1] || '-');
    parts.push(`{${id}}`);
  });
  return parts.join('') || '{seq}';
};

const readDrag = (event) => {
  try {
    return JSON.parse(event.dataTransfer.getData(DND) || '{}');
  } catch {
    return {};
  }
};

const IdChip = ({ id, onRemove, onDragStart, onDragOver, onDrop }) => {
  const meta = tokenMeta(id);
  const required = id === 'seq';
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className="inline-flex items-center gap-0.5 rounded-lg border border-slate-300 bg-white px-1.5 py-1 text-[11px] font-medium text-slate-800 shadow-sm cursor-grab active:cursor-grabbing"
    >
      <GripVertical className="w-3 h-3 text-slate-400 shrink-0" />
      <span>{meta.label}</span>
      {!required ? (
        <button
          type="button"
          onClick={onRemove}
          className="ml-0.5 p-0.5 rounded text-slate-400 hover:text-red-600"
          aria-label={`Remove ${meta.label}`}
        >
          <X className="w-3 h-3" />
        </button>
      ) : null}
    </div>
  );
};

const DocumentIdFormatBuilder = ({ label, value, onChange }) => {
  const parsed = useMemo(() => parseIdFormat(value), [value]);
  const [dropIndex, setDropIndex] = useState(null);
  const unused = ID_TOKENS.filter((t) => !parsed.tokens.includes(t.id));
  const preview = previewDocumentId(serializeIdFormat(parsed));

  const commit = (tokens, seps) => {
    onChange(serializeIdFormat({ tokens, seps }));
  };

  const defaultSep = parsed.seps[0] || '-';

  const insertToken = (tokenId, index) => {
    if (!TOKEN_IDS.includes(tokenId) || parsed.tokens.includes(tokenId)) return;
    const next = [...parsed.tokens];
    const at = Math.max(0, Math.min(index, next.length));
    next.splice(at, 0, tokenId);
    const seps = [...parsed.seps];
    seps.splice(at, 0, defaultSep);
    commit(next, seps.slice(0, Math.max(0, next.length - 1)));
  };

  const moveToken = (from, to) => {
    if (from < 0 || to < 0 || from === to || to === from + 1) return;
    const next = [...parsed.tokens];
    const [item] = next.splice(from, 1);
    const insertAt = from < to ? to - 1 : to;
    next.splice(insertAt, 0, item);
    commit(next, next.slice(1).map(() => defaultSep));
  };

  const removeToken = (index) => {
    if (parsed.tokens[index] === 'seq') return;
    const next = parsed.tokens.filter((_, i) => i !== index);
    commit(next, next.slice(1).map(() => defaultSep));
  };

  const cycleSep = (index) => {
    const seps = [...parsed.seps];
    const current = seps[index] || '-';
    seps[index] = SEPS[(SEPS.indexOf(current) + 1) % SEPS.length];
    commit(parsed.tokens, seps);
  };

  const handleDropAt = (event, index) => {
    event.preventDefault();
    setDropIndex(null);
    const payload = readDrag(event);
    if (payload.source === 'palette' && payload.token) {
      insertToken(payload.token, index);
      return;
    }
    if (payload.source === 'seq' && Number.isInteger(payload.index)) {
      moveToken(payload.index, index);
    }
  };

  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold text-gray-700">{label}</p>
      <p className="text-[11px] text-gray-500">
        Drag pieces into the row. Reorder by dragging. Number is always kept.
      </p>

      <div
        className="min-h-[52px] rounded-xl border border-dashed border-slate-300 bg-slate-50 px-2 py-2 flex flex-wrap items-center gap-1"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => handleDropAt(e, parsed.tokens.length)}
      >
        {parsed.tokens.map((id, index) => (
          <React.Fragment key={`${id}-${index}`}>
            <div
              className={`h-8 w-3 rounded ${dropIndex === index ? 'bg-blue-400' : 'bg-transparent'}`}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDropIndex(index);
              }}
              onDragLeave={() => setDropIndex((cur) => (cur === index ? null : cur))}
              onDrop={(e) => {
                e.stopPropagation();
                handleDropAt(e, index);
              }}
            />
            <IdChip
              id={id}
              onRemove={() => removeToken(index)}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDropIndex(index);
              }}
              onDrop={(e) => {
                e.stopPropagation();
                handleDropAt(e, index);
              }}
              onDragStart={(e) => {
                e.dataTransfer.setData(DND, JSON.stringify({ source: 'seq', index, token: id }));
                e.dataTransfer.effectAllowed = 'move';
              }}
            />
            {index < parsed.tokens.length - 1 ? (
              <button
                type="button"
                onClick={() => cycleSep(index)}
                className="px-1 text-[11px] font-mono text-slate-500 hover:text-slate-800"
                title="Change separator"
              >
                {parsed.seps[index] || '-'}
              </button>
            ) : null}
          </React.Fragment>
        ))}
        <div
          className={`h-8 w-1.5 rounded ${dropIndex === parsed.tokens.length ? 'bg-blue-500' : 'bg-transparent'}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDropIndex(parsed.tokens.length);
          }}
        />
      </div>

      <div className="flex flex-wrap gap-1.5">
        {unused.length ? unused.map((token) => (
          <button
            key={token.id}
            type="button"
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData(DND, JSON.stringify({ source: 'palette', token: token.id }));
              e.dataTransfer.effectAllowed = 'copy';
            }}
            onClick={() => insertToken(token.id, parsed.tokens.length)}
            className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-600 hover:border-blue-400 hover:text-blue-800 cursor-grab"
          >
            {token.label}
          </button>
        )) : (
          <span className="text-[11px] text-gray-400">All pieces are in the ID.</span>
        )}
      </div>

      <p className="text-[11px] text-gray-500">
        Example: <span className="font-mono text-gray-800">{preview}</span>
      </p>
    </div>
  );
};

export default DocumentIdFormatBuilder;
